import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
// The namespace, not named imports (see LayersImport): an older kit has no ReceivePage.
import * as kitUi from '@flobi/asset-library/ui';
import '@flobi/asset-library/ui.css';
import { tileUrl } from '../editor/content/tileUrl';
import { createFile, listFiles } from '../home/api';
import { relativeWhen, type FileRow } from '../home/rows';
import { useTheme } from '../theme';
import { familyNames, fontItemsOf, fontRows, fontsParam, sentFaces, type FontParcelItem } from './fontParcel';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const kit = kitUi as unknown as { ReceivePage?: ComponentType<any> };

/**
 * A font sent from Flobi Fonts (/receive?parcel=… of type `font`, cross-app pages 29 Oct 2026): into a new file or one
 * of yours. The choice opens the editor with ?fonts=…&family=…, and the editor keeps the faces with the file (EditorPage,
 * fontParcel.ts) — the family is then in the Font picker, ready to set.
 */
export function FontReceive({ parcel }: { parcel: { name: string; from: { app: string | null }; items?: unknown; payload?: unknown } }) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const items: FontParcelItem[] = useMemo(() => fontItemsOf(parcel), [parcel]);
  const { faces, leftOut } = useMemo(() => sentFaces(items), [items]);
  const families = useMemo(() => familyNames(items), [items]);
  const [files, setFiles] = useState<FileRow[] | null>(null);
  const [going, setGoing] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(faces.length ? null : 'Artwork takes TTF and OTF fonts from Flobi Fonts. Nothing here is one.');

  useEffect(() => {
    let live = true;
    listFiles({ limit: 60, scope: 'recent' })
      .then((rows) => live && setFiles(rows))
      .catch(() => live && setFiles([]));
    return () => { live = false; };
  }, []);

  const open = (documentId: string) => navigate(`/f/${encodeURIComponent(documentId)}?fonts=${fontsParam(faces)}&family=${encodeURIComponent(families[0] ?? '')}`, { replace: true });
  const startNew = async () => {
    if (!faces.length || going) return;
    setGoing('new');
    try {
      open((await createFile({ name: parcel.name || families[0] || 'Untitled' })).id);
    } catch (error) {
      setProblem((error as Error).message || 'A new file could not be made. Try again.');
      setGoing(null);
    }
  };

  const ReceivePage = kit.ReceivePage;
  if (!ReceivePage) return null;
  const names = families.join(', ') || 'The font';
  return (
    <ReceivePage
      app="artwork"
      mode={theme === 'light' ? 'light' : 'dark'}
      title="Open in Artwork"
      from={parcel.from.app ? { app: parcel.from.app, name: parcel.name } : null}
      problem={problem}
      arriving={{ glyph: 'doc', facts: [`${faces.length} ${faces.length === 1 ? 'style' : 'styles'}${leftOut ? ` · ${leftOut} left out` : ''}`, 'Kept with the file'], rows: fontRows(items) }}
      sentence={`${names} ${families.length > 1 ? 'go' : 'goes'} into the file's fonts, ready to choose in Font. Choose a new file or one of yours.${leftOut ? ' Artwork draws TTF and OTF files only.' : ''}`}
      newOption={faces.length ? { label: 'New file', hint: `A file of its own, with ${names} in its fonts`, busyLabel: 'Making the file…', onChoose: startNew } : undefined}
      existing={faces.length ? {
        heading: 'Add to one of your files',
        searchPlaceholder: 'Search your files',
        chip: 'Add here',
        glyph: 'board',
        loading: files === null,
        empty: 'No files yet. Start a new one above.',
        items: (files ?? []).map((row) => ({ id: row.id, name: row.name || 'Untitled', thumbnailUrl: row.thumbnailUrl ? tileUrl(row.thumbnailUrl, 640) : null, meta: relativeWhen(row.myLastEditAt || row.updatedAt) })),
        onChoose: (item: { id: string }) => {
          if (going) return;
          setGoing(item.id);
          open(item.id);
        },
      } : undefined}
      going={going}
      onBack={() => navigate('/')}
      backLabel="Artwork"
    />
  );
}
